import { cva, type VariantProps } from 'class-variance-authority';
import type { ReactElement } from 'react';
import { cn } from '../util/classes.ts';
import { PolymorphicButtonBase, type PolymorphicButtonBaseProps } from './button.tsx';

/**
 * Shared appearance of the toolbar-button family: shape, interaction states,
 * joined-sibling borders, and the `variant` tints. Exported so components that
 * are toolbar buttons with a different footprint — `ToolbarIconButton` —
 * inherit the same look instead of restating it.
 */
export const toolbarButtonVariants = cva(
    [
        'select-none inline-flex shrink-0 items-center justify-center gap-cozy whitespace-nowrap cursor-pointer',
        'h-interactive min-w-interactive rounded-default px-default',
        'state-disabled:pointer-events-none state-disabled:text-content-disabled state-disabled:bg-transparent',
        'transition duration-state ease-state',
        'active:translate-px',
        // border off-container only
        'not-l2l3:border border-line state-disabled:border-line-weak',
        'l2l3:rounded-tight l2l3:px-default',
        // joined children: square inner corners and overlap adjacent borders
        'not-l2l3:joined-trailing:rounded-l-none not-l2l3:joined-trailing:-ml-px',
        'not-l2l3:joined-leading:rounded-r-none',
        // raise active element so its border isn't covered by neighbors
        'not-l2l3:joined:focus-visible:relative not-l2l3:joined:focus-visible:z-10',
    ],
    {
        variants: {
            variant: {
                secondary: ['text-content', 'hover:bg-content/(--opacity-wash)'],
                primary: ['bg-brand/(--opacity-wash) text-brand', 'hover:bg-brand/(--opacity-wash-hover)'],
                ai: ['text-ai-2', 'hover:bg-ai-2/(--opacity-wash)'],
            },
        },
        defaultVariants: {
            variant: 'secondary',
        },
    },
);

export type ToolbarButtonVariantProps = VariantProps<typeof toolbarButtonVariants>;

export type ToolbarButtonProps = PolymorphicButtonBaseProps & ToolbarButtonVariantProps;

export function ToolbarButton({ className, variant, ...props }: ToolbarButtonProps): ReactElement {
    return <PolymorphicButtonBase className={cn(toolbarButtonVariants({ variant }), className)} {...props} />;
}
